import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, RefreshCw, Receipt, Clock, Trash2, CheckSquare, Square } from "lucide-react";
import { api, formatError, money, fmtDateTime } from "../../lib/api";
import { AdminHeader } from "../../components/AdminLayout";

export default function AdminPayments() {
  const [tab, setTab] = useState("pending");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [busyBulk, setBusyBulk] = useState(false);

  const load = useCallback((t = tab) => { setLoading(true); setSelected(new Set()); api.get(t === "history" ? "/admin/orders/history" : "/admin/orders").then((r) => setOrders(r.data)).catch(() => {}).finally(() => setLoading(false)); }, [tab]);
  useEffect(() => { load(tab); }, [load, tab]);

  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAll = () => setSelected((s) => (s.size === orders.length ? new Set() : new Set(orders.map((o) => o.id))));
  const deleteSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} payment record(s)? This cannot be undone.`)) return;
    setBusyBulk(true);
    try { const r = await api.post("/admin/orders/history/delete", { ids: [...selected] }); toast.success(`Deleted ${r.data.deleted} record(s)`); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusyBulk(false); }
  };
  const deleteAll = async () => {
    if (!window.confirm("Delete ALL payment history? This permanently removes every approved/rejected record and cannot be undone.")) return;
    setBusyBulk(true);
    try { const r = await api.post("/admin/orders/history/clear"); toast.success(`Deleted ${r.data.deleted} record(s)`); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusyBulk(false); }
  };

  const act = async (o, action) => {
    setBusyId(o.id);
    try {
      await api.post(`/admin/orders/${o.id}/${action}`);
      toast.success(action === "approve" ? `Approved — ${o.reference} activated` : `Rejected ${o.reference}`);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  return (
    <div>
      <AdminHeader title="Payment verification" subtitle="Review PayNow / PayLah proofs. Passes are issued only after you approve."
        action={<button onClick={() => load()} data-testid="refresh-orders" className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-sm"><RefreshCw size={15} /> Refresh</button>} />

      <div className="inline-flex rounded-full bg-white/5 border border-white/10 p-1 mb-5">
        {["pending", "history"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`payments-tab-${t}`} className={`px-5 py-1.5 rounded-full text-sm font-semibold capitalize transition-colors ${tab === t ? "bg-[#D4AF37] text-black" : "text-[#A19DB0]"}`}>{t}</button>
        ))}
      </div>

      {tab === "history" && !loading && orders.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="history-toolbar">
          <button onClick={toggleSelectAll} data-testid="select-all-history" className="flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm">
            {selected.size === orders.length ? <CheckSquare size={15} className="text-[#D4AF37]" /> : <Square size={15} />} {selected.size === orders.length ? "Deselect all" : "Select all"}
          </button>
          <button onClick={deleteSelected} disabled={selected.size === 0 || busyBulk} data-testid="delete-selected-history" className="flex items-center gap-1.5 bg-[#E57373]/15 text-[#E57373] rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-40">
            <Trash2 size={15} /> Delete selected ({selected.size})
          </button>
          <button onClick={deleteAll} disabled={busyBulk} data-testid="delete-all-history" className="flex items-center gap-1.5 bg-[#E57373]/15 text-[#E57373] rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-40 ml-auto">
            <Trash2 size={15} /> Delete all history
          </button>
        </div>
      )}

      {loading ? (
        <p className="text-[#A19DB0] text-sm">Loading…</p>
      ) : orders.length === 0 ? (
        <div className="text-center py-16 text-[#A19DB0]" data-testid="no-orders"><Receipt size={40} className="mx-auto mb-3 opacity-50" /><p>{tab === "history" ? "No processed payments yet." : "No payments to verify right now."}</p></div>
      ) : (
        <div className="grid gap-4">
          {orders.map((o) => (
            <div key={o.id} data-testid={`order-${o.id}`} className={`rounded-2xl border bg-white/5 p-5 ${selected.has(o.id) ? "border-[#D4AF37]" : "border-white/10"}`}>
              {tab === "history" && (
                <button onClick={() => toggleOne(o.id)} data-testid={`select-order-${o.id}`} className="mb-3 flex items-center gap-2 text-sm text-[#A19DB0]">
                  {selected.has(o.id) ? <CheckSquare size={18} className="text-[#D4AF37]" /> : <Square size={18} />} Select
                </button>
              )}
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display text-xl font-semibold tracking-wide text-[#D4AF37]" data-testid={`order-ref-${o.id}`}>{o.reference}</span>
                    {o.status === "submitted" && <span className="text-[10px] uppercase rounded-full px-2 py-0.5 bg-[#D4AF37]/20 text-[#D4AF37]">proof submitted</span>}
                    {o.code && <span data-testid={`order-code-${o.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold rounded-full px-2 py-0.5 bg-[#5CC8FF]/15 text-[#5CC8FF] border border-[#5CC8FF]/30">Code {o.code}</span>}
                  </div>
                  <p className="text-[#E8E4F0] text-sm mt-1 font-medium" data-testid={`order-buyer-${o.id}`}>{o.buyer_name || "Unknown member"}{o.buyer_email ? ` · ${o.buyer_email}` : ""}{o.buyer_phone ? ` · ${o.buyer_phone}` : ""}</p>
                  <p className="text-[#A19DB0] text-xs mt-1 flex items-center gap-1"><Clock size={12} /> {fmtDateTime(o.submitted_at || o.created_at)}{o.method ? ` · ${o.method}` : ""}</p>
                  <ul className="mt-3 space-y-1 text-sm">
                    {(o.items || []).map((it, i) => (
                      <li key={`${o.id}-item-${i}`} className="text-[#E8E4F0]">{it.name}{it.option ? ` · ${it.option}` : ""} <span className="text-[#A19DB0]">— {money(it.price)}</span></li>
                    ))}
                  </ul>
                  <div className="mt-2 text-sm text-[#A19DB0] space-y-0.5">
                    <div>Subtotal {money(o.subtotal)}{o.discount_percent ? ` (−${o.discount_percent}%)` : ""}</div>
                    {o.fee > 0 && <div>Fee {money(o.fee)}</div>}
                    {o.tax > 0 && <div>Tax {money(o.tax)}</div>}
                    <div className="text-[#E8E4F0] font-semibold">Total {money(o.total)}</div>
                  </div>
                </div>
                {o.proof_url && (
                  <button onClick={() => setPreview(o.proof_url)} data-testid={`view-proof-${o.id}`} className="shrink-0">
                    <img src={o.proof_url} alt="proof" className="w-24 h-24 object-cover rounded-xl border border-white/10 hover:border-[#D4AF37] transition-colors" />
                  </button>
                )}
              </div>
              {tab === "pending" ? (
                <div className="flex gap-2 mt-4">
                  <button onClick={() => act(o, "approve")} disabled={busyId === o.id || o.status !== "submitted"} data-testid={`approve-${o.id}`} className="flex items-center gap-1.5 bg-[#4ADE80]/15 text-[#4ADE80] rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-40"><Check size={15} /> Approve & activate</button>
                  <button onClick={() => act(o, "reject")} disabled={busyId === o.id} data-testid={`reject-${o.id}`} className="flex items-center gap-1.5 bg-[#E57373]/15 text-[#E57373] rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-40"><X size={15} /> Reject</button>
                </div>
              ) : (
                <div className="mt-4">
                  <span data-testid={`order-status-${o.id}`} className={`text-xs font-semibold rounded-full px-3 py-1 ${o.status === "paid" ? "bg-[#4ADE80]/15 text-[#4ADE80]" : "bg-[#E57373]/15 text-[#E57373]"}`}>{o.status === "paid" ? "Approved" : "Rejected"}{o.processed_at ? ` · ${fmtDateTime(o.processed_at)}` : ""}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {preview && (
        <div onClick={() => setPreview(null)} data-testid="proof-preview" className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6">
          <img src={preview} alt="proof full" className="max-w-full max-h-full rounded-2xl" />
        </div>
      )}
    </div>
  );
}
